import React from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import { DateTime } from "luxon";
import { isWithinBusinessHours } from "@/utils/restaurantCartUtils";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { OrderEstimates } from "@/dto/orderEstimates";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { Service } from "@/dto/service";
import { SessionStorageKey } from "@/utils/storageKeys";
import { OrderType } from "@/types";
import { DayPart } from "@/dto/dayPartMenu";
import { useTranslations } from "next-intl";
import { isOrderSchedulingEnabled } from "@/utils/scheduleOrderUtils";

export const ScheduledOrderDropdown = ({
  scheduledOrder,
  setScheduledOrder,
  showScheduleScreen,
  leadTime,
  merchant,
  orderType,
  selectedDayPart = null,
}: {
  scheduledOrder: any;
  setScheduledOrder: any;
  showScheduleScreen: () => void;
  leadTime: any;
  merchant: any;
  orderType: string;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("ScheduledOrderDropdown");
  const now = new Date();
  const isOpen = isOrderSchedulingEnabled(merchant) && isBusinessOpen(merchant);
  const currentDateTime = DateTime.fromJSDate(now, {
    zone: merchant.timezone,
  });
  const merchantService = merchant.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const businessHours = merchantService?.hours;
  const { deliveryTime, pickupTime } = getFromSessionStorage<
    Partial<OrderEstimates>
  >(SessionStorageKey.ORDER_ESTIMATES, {});

  const pickUpTimeDate = pickupTime ? new Date(pickupTime).getTime() : 0;
  const deliveryTimeDate = deliveryTime ? new Date(deliveryTime).getTime() : 0;

  const deliveryPickupTimeText = Math.round(
    (pickUpTimeDate + leadTime * 60000 - Date.now()) / 1000 / 60
  );

  const deliveryTimeText = Math.round(
    (deliveryTimeDate + leadTime * 60000 - Date.now()) / 1000 / 60
  );

  const asapTime =
    deliveryTime && orderType === OrderType.DELIVERY
      ? [
          "ASAP",
          `(${deliveryPickupTimeText}`,
          "-",
          deliveryTimeText,
          "mins)",
        ].join(" ")
      : "ASAP (In " + leadTime + " mins)";

  const canShowASAP = isWithinBusinessHours(
    currentDateTime,
    businessHours,
    merchant?.timezone,
    leadTime,
    selectedDayPart
  );

  const setAsapOrder = () => {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      asap: true,
      date: {
        displayValue: "Today",
        dateObject: new Date(),
      },
      time: {
        displayValue: "",
        timeObject: new Date(now),
      },
    });
    setScheduledOrder({
      type: "asap",
      epoch: currentDateTime.toMillis(),
    });
  };

  return (
    <Popover className="relative">
      <PopoverButton
        className={[
          "flex",
          "items-center",
          "gap-x-2",
          "w-full",
          "leading-6",
          "text-gray-900",
          "hover:bg-gray-50",
          "disabled:hover:bg-transparent",
          "disabled:cursor-default",
        ].join(" ")}
        disabled={false}
      >
        <input
          type="text"
          id="scheduled_order_input"
          className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border border-black hover:border-black focus:outline-none focus:ring-1 focus:ring-blue peer cursor-pointer disabled:border-transparent disabled:cursor-default"
          value={scheduledOrder?.type === "asap" ? asapTime : scheduledOrder}
          onChange={(e) => setScheduledOrder(e.target.value)}
          disabled={false}
        />
        <label
          htmlFor="scheduled_order_input"
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
        >
          {t("forWhen")}
        </label>
      </PopoverButton>
      <PopoverPanel
        transition
        className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="p-3">
          {canShowASAP && (
            <CloseButton
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50"
              onClick={() => {
                setAsapOrder();
              }}
            >
              <div className="flex-auto">
                <p className="text-[15px]">{asapTime}</p>
              </div>
            </CloseButton>
          )}
          {isOpen && (
            <CloseButton
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50"
              onClick={() => {
                showScheduleScreen();
              }}
            >
              <div className="flex-auto">
                <p className="text-[15px]">{t("scheduleOrder")}</p>
              </div>
            </CloseButton>
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
};
